import React, { useEffect, useRef, useState } from 'react'

import Menu, { MenuProps } from 'components/menu'
import Select from 'components/select'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { ICalendarSettings, ITableViewColumn } from 'types'

const CalendarMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  const columns = spreadsheetData.viewDetails.columns ?? []
  const dateColumns = columns.filter((c: ITableViewColumn) => DATE_COLUMN_TYPES.includes(c.kind))

  // Mirror the chart-menu pattern: prefer the in-progress userConfiguration
  // value, fall back to the saved viewDetails value, default to empty.
  const initialSettings: ICalendarSettings | null =
    spreadsheetData.userConfiguration.calendarSettings ?? spreadsheetData.viewDetails.calendarSettings

  const [settings, setSettings] = useState<ICalendarSettings>({
    labelColumnPublicId: initialSettings?.labelColumnPublicId ?? null,
    startColumnPublicId: initialSettings?.startColumnPublicId ?? null,
    endColumnPublicId: initialSettings?.endColumnPublicId ?? null
  })

  // Auto-save into userConfiguration on every change. The view is persisted
  // when the user hits the global "Save view" button.
  const didInit = useRef(false)
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      return
    }
    if (JSON.stringify(settings) === JSON.stringify(spreadsheetData.userConfiguration.calendarSettings ?? null)) {
      return
    }
    setSpreadsheetData({ type: 'SET_CALENDAR_SETTINGS', calendarSettings: settings })
  }, [settings])

  const labelOptions = columns.map((c: ITableViewColumn) => ({ value: c.publicId, label: c.name }))
  const dateOptions = dateColumns.map((c: ITableViewColumn) => ({ value: c.publicId, label: c.name }))

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div className="flex flex-col gap-3 p-4">
        <div className="text-base font-semibold text-body">Calendar Settings</div>
        <Select
          label="Label column"
          inputId="calendar-label-col"
          options={labelOptions}
          optionsSelected={settings.labelColumnPublicId ? [settings.labelColumnPublicId] : []}
          onOptionClick={(value) => setSettings((s) => ({ ...s, labelColumnPublicId: value || null }))}
          placeholder="Select a column"
        />
        <Select
          label="Start date column"
          inputId="calendar-start-col"
          options={dateOptions}
          optionsSelected={settings.startColumnPublicId ? [settings.startColumnPublicId] : []}
          onOptionClick={(value) => setSettings((s) => ({ ...s, startColumnPublicId: value || null }))}
          placeholder="Select a date column"
          noOptionsMessage={<span>No date or datetime columns available</span>}
        />
        <Select
          label="End date column (optional)"
          inputId="calendar-end-col"
          options={dateOptions}
          optionsSelected={settings.endColumnPublicId ? [settings.endColumnPublicId] : []}
          onOptionClick={(value) => setSettings((s) => ({ ...s, endColumnPublicId: value || null }))}
          placeholder="None"
          clearable
          noOptionsMessage={<span>No date or datetime columns available</span>}
        />
      </div>
    </Menu>
  )
}

export default CalendarMenu
